import { FormattedMessage } from '@alltrails/shared/react-intl';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import Modal from 'components/Modal';
import Typography from '@alltrails/shared/denali/components/Typography';
import Logo from '@alltrails/shared/denali/components/Logo';
import useUser from 'hooks/useUser';
import logWebProModalPurchaseButtonTapped from '@alltrails/analytics/events/logWebProModalPurchaseButtonTapped';
import WebProModalPurchaseButtonCopyText from '@alltrails/analytics/enums/WebProModalPurchaseButtonCopyText';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import Button from '@alltrails/shared/denali/components/Button';
import { useEffect } from 'react';
import logWebProModalShown from '@alltrails/analytics/events/logWebProModalShown';
import * as styles from './FlyoverUpsell2.module.scss';

type Props = {
  isOpen: boolean;
  onClick: () => void;
  onRequestClose: () => void;
};

// Desktop https://www.figma.com/file/UsRZiiso2irMJYKykeo6xK/On-page-web-checkout?type=design&node-id=568-141893&t=023Mi50jf1ysrOTt-4
// Mobile https://www.figma.com/file/UsRZiiso2irMJYKykeo6xK/On-page-web-checkout?type=design&node-id=568-141902&t=023Mi50jf1ysrOTt-4
export default function FlyoverUpsell2({ isOpen, onClick, onRequestClose }: Props) {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const isPromoEligible = !user || user.isPromoEligible;
  const { plan } = __AT_DATA__;

  useEffect(() => {
    if (isOpen) {
      logWebProModalShown({
        purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
        trigger: CarouselDisplayTrigger.Flyover
      });
    }
  }, [isPromoEligible, isOpen]);

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: __AT_DATA__.currencyCode
  });

  const text = isPromoEligible ? (
    <FormattedMessage defaultMessage="Start your free trial" />
  ) : (
    <FormattedMessage defaultMessage="Upgrade to AllTrails+" />
  );

  const finePrint = isPromoEligible ? (
    <FormattedMessage
      defaultMessage="Get 7 days free, then only {annualCost}/year (that’s {monthlyCost}/month).{br} Cancel anytime."
      values={{
        annualCost: formatter.format(Number(plan.priceTotal)),
        br: <br className={styles.br} />,
        monthlyCost: formatter.format(Number(plan.priceMonth))
      }}
    />
  ) : (
    <FormattedMessage
      defaultMessage="Only {annualCost}/year (that’s {monthlyCost}/month).{br} Cancel anytime."
      values={{
        annualCost: formatter.format(Number(plan.priceTotal)),
        br: <br className={styles.br} />,
        monthlyCost: formatter.format(Number(plan.priceMonth))
      }}
    />
  );

  const testId = 'upsell-cta';

  const handleOnClick = () => {
    logWebProModalPurchaseButtonTapped({
      copy_text: isPromoEligible ? WebProModalPurchaseButtonCopyText.StartYourFreeTrial : WebProModalPurchaseButtonCopyText.UpgradeToAllTrailsPlus,
      purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
      trigger: CarouselDisplayTrigger.Flyover
    });

    onClick();
  };

  return (
    <Modal
      baseCtas={{
        finePrint: <Typography variant="text100">{finePrint}</Typography>,
        orientation: 'vertical',
        primaryCta: {
          onClick: handleOnClick,
          text,
          testId
        }
      }}
      closeButtonStyle={styles.close}
      isOpen={isOpen}
      headerClassName={styles.header}
      onRequestClose={onRequestClose}
      size="auto"
      interiorClassName={styles.interior}
      footerClassName={styles.footer}
    >
      <div className={styles.container}>
        <div className={styles.img} />
        <div className={styles.content}>
          <Logo color="dark" size="xs" variant="plus-logomark" />
          <Typography component="div" variant="heading600">
            <FormattedMessage defaultMessage="Preview any trail with a 3D flyover" />
          </Typography>
          <Typography component="div" variant="text200">
            <FormattedMessage defaultMessage="Immerse yourself in a 3D preview to anticipate every twist and turn. Get AllTrails+ to unlock this feature and more." />
          </Typography>
          <Button className={styles.button} onClick={handleOnClick} testId={testId} text={text} variant="primary" />
          <div className={styles.fine}>{finePrint}</div>
        </div>
      </div>
    </Modal>
  );
}
